/**
 * Paints streamed AI text smoothly.
 * - First non-empty update is applied immediately (no rAF wait).
 * - Large jumps are eased in so a fat SSE chunk doesn't appear as one blob.
 */
export const createStreamPainter = (onPaint) => {
  let shown = "";
  let target = "";
  let finished = false;
  let rafId = null;
  let cancelled = false;

  const paint = (text, isDone) => {
    if (cancelled) return;
    onPaint(text, isDone);
  };

  const stop = () => {
    if (rafId != null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  const tick = () => {
    rafId = null;
    if (cancelled) return;

    if (shown === target) {
      if (finished) paint(shown, true);
      return;
    }

    const backlog = target.length - shown.length;
    // Catch up faster when far behind; stay gentle for small gaps.
    const step = Math.min(
      backlog,
      Math.max(3, Math.min(48, Math.ceil(backlog / 4))),
    );
    shown = target.slice(0, shown.length + step);
    const isComplete = finished && shown === target;
    paint(shown, isComplete);

    if (!isComplete && shown !== target) {
      rafId = requestAnimationFrame(tick);
    }
  };

  const ensureTicking = () => {
    if (rafId == null) rafId = requestAnimationFrame(tick);
  };

  return {
    /** @param {string} next @param {boolean} [isDone] */
    push(next, isDone = false) {
      if (cancelled) return;
      target = typeof next === "string" ? next : "";
      finished = Boolean(isDone);

      if (!shown && target) {
        // Instant first paint — critical for time-to-first-token UX.
        shown = target.length <= 32 ? target : target.slice(0, 24);
        paint(shown, finished && shown === target);
        if (shown !== target) ensureTicking();
        else if (finished) paint(shown, true);
        return;
      }

      if (finished && target.length - shown.length <= 64) {
        shown = target;
        stop();
        paint(shown, true);
        return;
      }

      ensureTicking();
    },
    cancel() {
      cancelled = true;
      stop();
    },
  };
};
